import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Check, X, Info, Shield } from 'lucide-react';
import { Button, Input, TextArea, toast } from '../../components/common';
import { roleApi } from '../../services/api';

export default function RoleForm({ role, allPermissions, rolePermissions, onSuccess, onCancel }) {
    const isEditing = !!role;
    const [activeTab, setActiveTab] = useState('info');

    const [form, setForm] = useState({
        name: '',
        description: ''
    });
    const [errors, setErrors] = useState({});
    const [selectedPermissions, setSelectedPermissions] = useState([]);

    // Initialize form when role changes
    useEffect(() => {
        if (role) {
            setForm({
                name: role.name || '',
                description: role.description || ''
            });
        } else {
            setForm({ name: '', description: '' });
        }
    }, [role]);

    // Initialize selected permissions when rolePermissions changes
    useEffect(() => {
        // Use rolePermissions prop first, then fallback to role.permissions
        const permsToUse = rolePermissions || role?.permissions;

        if (permsToUse && Array.isArray(permsToUse) && permsToUse.length > 0) {
            const permIds = permsToUse.map(p => p.id);
            console.log('Setting selected permissions:', permIds.length);
            setSelectedPermissions(permIds);
        }
        // Don't reset to empty array - only update when we have data
    }, [rolePermissions, role?.permissions]);

    const mutation = useMutation({
        mutationFn: (data) => isEditing
            ? roleApi.update(role.id, data)
            : roleApi.create(data),
        onSuccess: () => {
            toast.success(`Role ${isEditing ? 'updated' : 'created'} successfully`);
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save role');
        }
    });

    const permissionMutation = useMutation({
        mutationFn: (permissionIds) => roleApi.updatePermissions(role.id, { permissionIds }),
        onSuccess: () => {
            toast.success('Permissions updated successfully');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to update permissions');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleTogglePermission = (permissionId) => {
        setSelectedPermissions(prev =>
            prev.includes(permissionId)
                ? prev.filter(id => id !== permissionId)
                : [...prev, permissionId]
        );
    };

    const handleSelectAllModule = (modulePerms) => {
        const permIds = modulePerms.map(p => p.id);
        const allSelected = permIds.every(id => selectedPermissions.includes(id));

        if (allSelected) {
            setSelectedPermissions(prev => prev.filter(id => !permIds.includes(id)));
        } else {
            setSelectedPermissions(prev => [...new Set([...prev, ...permIds])]);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!form.name?.trim()) newErrors.name = 'Role name is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        mutation.mutate(form);
    };

    const handleSavePermissions = () => {
        permissionMutation.mutate(selectedPermissions);
    };

    const groupedPermissions = allPermissions?.grouped || {};

    // Styles
    const tabStyle = (isActive) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1rem',
        background: isActive ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : '#f8fafc',
        border: `1px solid ${isActive ? '#2563eb' : '#e2e8f0'}`,
        borderRadius: '0.5rem',
        color: isActive ? 'white' : '#64748b',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxShadow: isActive ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
    });

    const tabCountStyle = (isActive) => ({
        background: isActive ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
        padding: '0.125rem 0.5rem',
        borderRadius: '1rem',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: isActive ? 'white' : '#64748b'
    });

    // For creating new role (no tabs, just the form)
    if (!isEditing) {
        return (
            <form onSubmit={handleSubmit} style={{ padding: '0.5rem 0' }}>
                <div className="form-stack">
                    <Input
                        name="name"
                        label="Role Name"
                        placeholder="e.g., Procurement Manager"
                        value={form.name}
                        onChange={handleChange}
                        error={errors.name}
                        required
                    />
                    <TextArea
                        name="description"
                        label="Description"
                        placeholder="Brief description of this role"
                        value={form.description}
                        onChange={handleChange}
                        rows={3}
                    />
                </div>
                <div className="form-actions">
                    <Button variant="secondary" type="button" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" loading={mutation.isPending}>
                        Create Role
                    </Button>
                </div>
            </form>
        );
    }

    // Editing mode with custom tabs
    return (
        <div style={{ minHeight: '450px' }}>
            {/* Custom Tab Headers */}
            <div style={{ display: 'flex', gap: '0.5rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0', marginBottom: '1.25rem' }}>
                <button
                    type="button"
                    style={tabStyle(activeTab === 'info')}
                    onClick={() => setActiveTab('info')}
                >
                    <Info size={16} />
                    <span>Role Info</span>
                </button>
                <button
                    type="button"
                    style={tabStyle(activeTab === 'permissions')}
                    onClick={() => setActiveTab('permissions')}
                >
                    <Shield size={16} />
                    <span>Permissions</span>
                    <span style={tabCountStyle(activeTab === 'permissions')}>{selectedPermissions.length}</span>
                </button>
            </div>

            {/* Tab Content */}
            <div style={{ minHeight: '350px' }}>
                {activeTab === 'info' && (
                    <form onSubmit={handleSubmit}>
                        <div className="form-stack">
                            <Input
                                name="name"
                                label="Role Name"
                                placeholder="e.g., Procurement Manager"
                                value={form.name}
                                onChange={handleChange}
                                error={errors.name}
                                disabled={role?.isSystemRole}
                                required
                            />
                            {role?.isSystemRole && (
                                <p style={{
                                    fontSize: '0.75rem',
                                    color: '#f59e0b',
                                    margin: '-0.5rem 0 0.5rem',
                                    padding: '0.5rem',
                                    background: '#fffbeb',
                                    borderRadius: '0.375rem',
                                    border: '1px solid #fef3c7'
                                }}>
                                    ⚠️ System role name cannot be changed
                                </p>
                            )}
                            <TextArea
                                name="description"
                                label="Description"
                                placeholder="Brief description of this role"
                                value={form.description}
                                onChange={handleChange}
                                rows={3}
                            />
                        </div>
                        <div className="form-actions">
                            <Button variant="secondary" type="button" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button type="submit" loading={mutation.isPending}>
                                Update Role
                            </Button>
                        </div>
                    </form>
                )}

                {activeTab === 'permissions' && (
                    <div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem',
                            paddingBottom: '0.75rem',
                            borderBottom: '1px solid #e2e8f0'
                        }}>
                            <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                                <strong style={{ color: '#3b82f6', fontSize: '1.25rem' }}>{selectedPermissions.length}</strong> permissions selected
                            </p>
                            <Button
                                size="sm"
                                onClick={handleSavePermissions}
                                loading={permissionMutation.isPending}
                            >
                                Save Permissions
                            </Button>
                        </div>

                        {Object.keys(groupedPermissions).length === 0 ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '3rem',
                                color: '#94a3b8',
                                textAlign: 'center',
                                background: '#f8fafc',
                                borderRadius: '0.75rem',
                                border: '1px dashed #e2e8f0'
                            }}>
                                No permissions available. Please check your access rights.
                            </div>
                        ) : (
                            <div style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                                {Object.entries(groupedPermissions).map(([module, modulePerms]) => {
                                    const perms = Array.isArray(modulePerms) ? modulePerms : [];
                                    const selectedCount = perms.filter(p => selectedPermissions.includes(p.id)).length;
                                    const allSelected = perms.length > 0 && selectedCount === perms.length;
                                    const someSelected = selectedCount > 0 && selectedCount < perms.length;

                                    return (
                                        <div key={module} style={{
                                            marginBottom: '1rem',
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '0.75rem',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '0.75rem 1rem',
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                                color: 'white'
                                            }}>
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    cursor: 'pointer',
                                                    fontWeight: 600,
                                                    fontSize: '0.8125rem',
                                                    letterSpacing: '0.05em'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={allSelected}
                                                        ref={input => {
                                                            if (input) input.indeterminate = someSelected;
                                                        }}
                                                        onChange={() => handleSelectAllModule(perms)}
                                                        style={{ width: '16px', height: '16px', accentColor: 'white', cursor: 'pointer' }}
                                                    />
                                                    <span>{module.toUpperCase()}</span>
                                                </label>
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    background: 'rgba(255,255,255,0.2)',
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '1rem'
                                                }}>
                                                    {selectedCount}/{perms.length}
                                                </span>
                                            </div>
                                            <div style={{
                                                padding: '0.75rem',
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                                gap: '0.5rem'
                                            }}>
                                                {perms.map((perm) => {
                                                    const isSelected = selectedPermissions.includes(perm.id);
                                                    return (
                                                        <label
                                                            key={perm.id}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.5rem',
                                                                padding: '0.5rem 0.75rem',
                                                                background: isSelected ? '#f0fdf4' : 'white',
                                                                border: `1px solid ${isSelected ? '#22c55e' : '#e2e8f0'}`,
                                                                borderRadius: '0.5rem',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleTogglePermission(perm.id)}
                                                                style={{ display: 'none' }}
                                                            />
                                                            <span style={{
                                                                width: '20px',
                                                                height: '20px',
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                                background: isSelected ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : '#fee2e2',
                                                                color: isSelected ? 'white' : '#dc2626'
                                                            }}>
                                                                {isSelected ? <Check size={12} /> : <X size={12} />}
                                                            </span>
                                                            <span style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', minWidth: 0, flex: 1 }}>
                                                                <span style={{
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 600,
                                                                    color: '#1e293b',
                                                                    fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace"
                                                                }}>{perm.code}</span>
                                                                <span style={{
                                                                    fontSize: '0.6875rem',
                                                                    color: '#64748b',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>{perm.name}</span>
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

RoleForm.propTypes = {
    role: PropTypes.object,
    allPermissions: PropTypes.object,
    rolePermissions: PropTypes.array,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
